import React, { useCallback, useMemo, useState } from "react";
import { MAX_COUNTERPARTY_CONTACTS_PER_COUNTERPARTY } from "@common/constants/counterparty";
import {
    CounterpartyContact,
    CreateCounterpartyContactBody,
} from "@common/schemas/counterpartyContact.schema";
import { View } from "@common/types/views/views";
import { CounterpartyContactsViewSchema } from "@common/types/views/counterpartyContactsView";
import {
    useCreateCounterpartyContactMutation,
    useDeleteCounterpartyContactMutation,
    useSetPrimaryCounterpartyContactMutation,
    useUpdateCounterpartyContactMutation,
} from "@api/counterparty.api";
import { isViewOnlyRefusalError } from "@lib/viewOnlyRefusal";
import { Button } from "@design-system/Button/Button";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import type { ViewTableMeta } from "@design-system/ViewTable/ViewTable.lib";
import formStyles from "@components/counterparty/CounterpartyForm.module.scss";
import {
    formatCounterpartyContactHeadline,
    formatCounterpartyContactName,
} from "@components/counterparty/counterpartyBookFormat";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { CounterpartyContactModal } from "./CounterpartyContactModal";
import {
    CounterpartyBookTableCallbacks,
    CounterpartyBookTableOptions,
} from "./counterpartyBookTableMeta";
import { createCounterpartyContactsView } from "./createCounterpartyBookViews";
import { createCounterpartyContactsViewConfig } from "./counterpartyContactsViewConfig";
import { matchesCounterpartyBookSearch } from "./matchesCounterpartyBookSearch";
import { preventSearchImplicitSubmit } from "./preventSearchImplicitSubmit";
import { isOcrSeedId } from "./ocrSeedRow";
import { LocalBookHandlers } from "./useOcrSeedBooks";
import { useCounterpartyBookEditor } from "./useCounterpartyBookEditor";
import styles from "./CounterpartyEditorDrawer.module.scss";

interface Props {
    counterpartyId: string;
    contacts: CounterpartyContact[];
    isReadOnly?: boolean;
    resolveWritableCounterpartyId?: () => Promise<string>;
    resolveWritableContact?: (
        contact: CounterpartyContact,
    ) => Promise<{ counterpartyId: string; contactId: string }>;
    onEditingChange?: (isEditing: boolean) => void;
    localBook?: LocalBookHandlers<CreateCounterpartyContactBody>;
    suggestedIds?: readonly string[];
    removedIds?: readonly string[];
}

export const CounterpartyContactsSection: React.FC<Props> = ({
    counterpartyId,
    contacts,
    isReadOnly = false,
    resolveWritableCounterpartyId,
    resolveWritableContact,
    onEditingChange,
    localBook,
    suggestedIds = [],
    removedIds = [],
}) => {
    const toaster = useToaster();
    const [search, setSearch] = useState("");
    const [view, setView] = useState(createCounterpartyContactsView);
    const {
        editingRow: editingContact,
        isModalOpen,
        deletingRow: deletingContact,
        closeModal,
        startAdd,
        startEdit,
        startDelete,
        clearDeleting,
    } = useCounterpartyBookEditor({
        rows: contacts,
        onEditingChange,
    });
    const viewConfig = useMemo(
        () => createCounterpartyContactsViewConfig(),
        [],
    );

    const createContact = useCreateCounterpartyContactMutation();
    const updateContact = useUpdateCounterpartyContactMutation();
    const deleteContact = useDeleteCounterpartyContactMutation();
    const setPrimary = useSetPrimaryCounterpartyContactMutation();
    const resolveCounterpartyId = useCallback(
        () =>
            resolveWritableCounterpartyId
                ? resolveWritableCounterpartyId()
                : Promise.resolve(counterpartyId),
        [counterpartyId, resolveWritableCounterpartyId],
    );
    const resolveContact = useCallback(
        (contact: CounterpartyContact) =>
            resolveWritableContact
                ? resolveWritableContact(contact)
                : Promise.resolve({ counterpartyId, contactId: contact.id }),
        [counterpartyId, resolveWritableContact],
    );

    const filteredContacts = useMemo(
        () =>
            contacts.filter((contact) =>
                matchesCounterpartyBookSearch(
                    [
                        formatCounterpartyContactHeadline(contact),
                        formatCounterpartyContactName(contact),
                        contact.email,
                        contact.phoneNumber,
                        contact.label,
                    ],
                    search,
                ),
            ),
        [contacts, search],
    );

    const tableMeta = useMemo<
        ViewTableMeta<
            CounterpartyBookTableCallbacks,
            CounterpartyBookTableOptions
        >
    >(
        () => ({
            callbacks: {
                onEdit: startEdit,
                onDelete: startDelete,
                onMakePrimary: (id) => {
                    const contact = contacts.find((row) => row.id === id);
                    if (!contact) {
                        return;
                    }
                    if (isOcrSeedId(contact.id)) {
                        localBook?.makePrimary(contact.id);
                        return;
                    }
                    void resolveContact(contact)
                        .then(({ counterpartyId: writableId, contactId }) =>
                            setPrimary.mutateAsync({
                                counterpartyId: writableId,
                                contactId,
                            }),
                        )
                        .catch((error) => {
                            if (!isViewOnlyRefusalError(error)) {
                                toaster.error(
                                    "Failed to make the contact primary",
                                );
                            }
                        });
                },
            },
            options: {
                isReadOnly,
                isDisabled: false,
                removedIds,
            },
        }),
        [
            contacts,
            isReadOnly,
            removedIds,
            resolveContact,
            setPrimary,
            startDelete,
            startEdit,
            toaster,
            localBook,
        ],
    );

    const handleViewChange = useCallback((next: View) => {
        setView(CounterpartyContactsViewSchema.parse(next));
    }, []);

    const canAdd =
        !isReadOnly &&
        contacts.length < MAX_COUNTERPARTY_CONTACTS_PER_COUNTERPARTY;

    return (
        <>
            <section
                className={formStyles.section}
                onKeyDown={preventSearchImplicitSubmit}
            >
                <h4 className={styles.bookSectionTitle}>Contacts</h4>
                <TablePageHeader
                    search={search}
                    onSearchChange={setSearch}
                    searchPlaceholder="Search contacts..."
                    searchKeyboardShortcuts={false}
                >
                    {!isReadOnly && (
                        <Button
                            type="button"
                            variant="primary"
                            size="md"
                            onClick={startAdd}
                            disabled={!canAdd}
                        >
                            Add contact
                        </Button>
                    )}
                </TablePageHeader>
                <ViewTable
                    data={filteredContacts}
                    view={view}
                    config={viewConfig}
                    onViewChange={handleViewChange}
                    tableMeta={tableMeta}
                    emptyView={() => (
                        <p className={styles.emptyState}>
                            {search
                                ? "No matching contacts"
                                : "No contacts yet"}
                        </p>
                    )}
                    enableKeyboardShortcuts={false}
                    rowBorders={false}
                    outerBorder
                    className={styles.bookTable}
                    getRowClassName={(contact: CounterpartyContact) => {
                        if (removedIds.includes(contact.id)) {
                            return styles.replacedRow;
                        }
                        if (suggestedIds.includes(contact.id)) {
                            return styles.suggestedRow;
                        }
                        return undefined;
                    }}
                />
            </section>
            <CounterpartyContactModal
                show={isModalOpen}
                contact={editingContact}
                onHide={closeModal}
                onSubmit={async (body) => {
                    if (editingContact && isOcrSeedId(editingContact.id)) {
                        localBook?.save(editingContact.id, body);
                        closeModal();
                        return;
                    }
                    if (editingContact) {
                        const { counterpartyId: writableId, contactId } =
                            await resolveContact(editingContact);
                        await updateContact.mutateAsync({
                            counterpartyId: writableId,
                            contactId,
                            body,
                        });
                    } else {
                        const writableId = await resolveCounterpartyId();
                        await createContact.mutateAsync({
                            counterpartyId: writableId,
                            body,
                        });
                    }
                    closeModal();
                }}
            />
            <ConfirmationModal
                show={deletingContact !== null}
                title="Delete contact"
                question={
                    deletingContact?.isPrimary
                        ? "Delete this primary contact? The next-oldest contact becomes primary."
                        : "Delete this contact?"
                }
                yes="Delete"
                yesBtnVariant="destructive"
                onConfirm={async () => {
                    if (!deletingContact) {
                        return;
                    }
                    if (isOcrSeedId(deletingContact.id)) {
                        localBook?.remove(deletingContact.id);
                        clearDeleting();
                        return;
                    }
                    const { counterpartyId: writableId, contactId } =
                        await resolveContact(deletingContact);
                    await deleteContact.mutateAsync({
                        counterpartyId: writableId,
                        contactId,
                    });
                    clearDeleting();
                }}
                onHide={clearDeleting}
            />
        </>
    );
};
